import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { aiDeleteSchedule, aiDevices, aiRunSchedule, aiSaveSchedule, aiSchedulesQuery, type AiDevice, type AiSchedule } from '../api/assistant'
import { Content } from '../ui/Shell'
import { Spinner } from '../ui/Spinner'
import { toast } from '../ui/notify'
import { SettingsCheck } from '../ui/SettingsParts'
import './schedules.css'

/**
 * Scheduled questions - `Assistant::schedules` / `views/assistant/schedules.php`:
 * lista programarilor omului (stanga) si formularul (dreapta). Agentul raspunde
 * pe program, numai in citire, si trimite raportul pe mail.
 */

const CADENCES = {
  daily: 'Every day',
  weekly: 'Every week',
  monthly: 'Every month',
} as const
const WEEKDAYS = ['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

type Draft = {
  id: number
  title: string
  question: string
  device_id: number
  device_name: string
  cadence: keyof typeof CADENCES
  time: string
  weekday: number
  month_day: number
  recipients: string
  enabled: boolean
}

function blank(prefill: { question: string; device_id: number }, myEmail: string): Draft {
  return {
    id: 0,
    title: '',
    question: prefill.question,
    device_id: prefill.device_id,
    device_name: prefill.device_id ? `#${prefill.device_id}` : '',
    cadence: 'weekly',
    time: '08:00',
    weekday: 1,
    month_day: 1,
    recipients: myEmail,
    enabled: true,
  }
}

export function SchedulesPage({ q, deviceId }: { q: string; deviceId: number }) {
  const queries = useQueryClient()
  const page = useQuery(aiSchedulesQuery(q, deviceId))
  const [draft, setDraft] = useState<Draft | null>(null)
  const [found, setFound] = useState<AiDevice[]>([])

  if (page.error) {
    return (
      <Content>
        <div className="alert alert-danger" style={{ margin: 16 }}>
          {(page.error as Error).message}
        </div>
      </Content>
    )
  }
  if (!page.data)
    return (
      <Content>
        <Spinner />
      </Content>
    )
  const data = page.data
  const d = draft ?? blank(data.prefill, data.myEmail)
  const patch = (part: Partial<Draft>) => setDraft({ ...d, ...part })
  const refresh = () => queries.invalidateQueries({ queryKey: ['assistant', 'schedules'] })

  const edit = (r: AiSchedule) =>
    setDraft({
      id: r.ID,
      title: r.title,
      question: r.question,
      device_id: r.device_id,
      device_name: r.device_name ?? (r.device_id ? `#${r.device_id}` : ''),
      cadence: r.cadence,
      time: `${String(r.run_hour).padStart(2, '0')}:${String(r.run_minute).padStart(2, '0')}`,
      weekday: r.weekday || 1,
      month_day: r.month_day || 1,
      recipients: r.recipients,
      enabled: r.enabled === 1,
    })

  const save = async (e: FormEvent) => {
    e.preventDefault()
    const [hour, minute] = d.time.split(':').map((x) => Number(x) || 0)
    const out = await aiSaveSchedule({
      id: d.id,
      title: d.title,
      question: d.question,
      device_id: d.device_id,
      cadence: d.cadence,
      run_hour: hour ?? 8,
      run_minute: minute ?? 0,
      weekday: d.weekday,
      month_day: d.month_day,
      recipients: d.recipients,
      enabled: d.enabled ? 1 : 0,
    })
    if (!out.ok) {
      toast(out.error ?? 'The schedule could not be saved.', 'Scheduled questions', 'error')
      return
    }
    toast('Saved.', 'Scheduled questions')
    setDraft(null)
    await refresh()
  }

  return (
    <Content>
      <div id="content">
        <div className="row">
          <div className="col-lg-12">
            {!data.ready ? (
              <div className="ai-sched-warn">
                The scheduling table does not exist yet: run the <code>2026_09_03_100002_ai_schedules_runbooks</code> migration. Until then nothing here can be
                saved.
              </div>
            ) : null}
            <div className="ai-sched">
              <div className="ai-sched-card">
                <h4>Your scheduled questions</h4>
                {data.rows.length === 0 ? (
                  <div className="ai-sched-empty">
                    None yet. Write a question on the right - the agent will answer it on schedule, in read-only mode, and send the answer by mail. Every run
                    also lands in your conversation history.
                  </div>
                ) : null}
                {data.rows.map((r) => (
                  <div key={r.ID} className={`ai-sched-row ${r.enabled === 1 ? '' : 'ai-sched-off'}`} data-id={r.ID}>
                    <div className="ai-sched-main">
                      <div className="ai-sched-title">{r.title}</div>
                      <div className="ai-sched-q">{r.question.slice(0, 400)}</div>
                      <div className="ai-sched-meta">
                        {CADENCES[r.cadence] ?? r.cadence}
                        {r.cadence === 'weekly' ? ` on ${WEEKDAYS[r.weekday] ?? ''}` : ''}
                        {r.cadence === 'monthly' ? ` on day ${r.month_day}` : ''}
                        {' at '}
                        {String(r.run_hour).padStart(2, '0')}:{String(r.run_minute).padStart(2, '0')}
                        {' · '}
                        {r.device_id > 0 ? `machine ${r.device_name || `#${r.device_id}`}` : 'whole fleet'}
                        {' · to '}
                        {r.recipients !== '' ? r.recipients : <em>nobody (portal only)</em>}
                        <br />
                        {r.enabled !== 1 ? 'paused' : `next ${r.next_run_at ?? ''}`}
                        {r.last_run_at ? ` · last ${r.last_run_at} (${r.last_status || '?'})` : ''}
                      </div>
                    </div>
                    <div className="ai-sched-acts">
                      <button
                        type="button"
                        className="btn btn-default ai-sched-run"
                        title="Queue it for the next minute"
                        onClick={() =>
                          void aiRunSchedule(r.ID).then(() => {
                            toast('Queued for the next minute.', 'Scheduled questions')
                            void refresh()
                          })
                        }
                      >
                        Run now
                      </button>
                      <button type="button" className="btn btn-default ai-sched-edit" onClick={() => edit(r)}>
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn btn-default ai-sched-del"
                        onClick={() => {
                          if (window.confirm('Delete this schedule?')) void aiDeleteSchedule(r.ID).then(refresh)
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="ai-sched-card ai-sched-form">
                <h4>{d.id > 0 ? 'Edit the schedule' : 'New scheduled question'}</h4>
                <form onSubmit={(e) => void save(e)}>
                  <div className="form-group">
                    <label htmlFor="aiSchedTitle">Title</label>
                    <input
                      type="text"
                      className="form-control"
                      id="aiSchedTitle"
                      maxLength={190}
                      placeholder="Missing critical patches, weekly"
                      value={d.title}
                      onChange={(e) => patch({ title: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="aiSchedQuestion">Question</label>
                    <textarea
                      className="form-control"
                      id="aiSchedQuestion"
                      rows={4}
                      value={d.question}
                      onChange={(e) => patch({ question: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="aiSchedDevice">Machine</label>
                    <input
                      type="text"
                      className="form-control"
                      id="aiSchedDevice"
                      placeholder="Whole fleet - or type a machine name"
                      autoComplete="off"
                      value={d.device_name}
                      onChange={(e) => {
                        patch({ device_name: e.target.value, device_id: 0 })
                        if (e.target.value.trim() !== '')
                          void aiDevices(e.target.value)
                            .then((o) => setFound(o.devices))
                            .catch(() => setFound([]))
                        else setFound([])
                      }}
                    />
                    {found.length > 0 ? (
                      <div className="list-group" style={{ marginTop: 4 }}>
                        {found.slice(0, 8).map((dev) => (
                          <button
                            key={dev.id}
                            type="button"
                            className="list-group-item"
                            onClick={() => {
                              patch({
                                device_id: dev.id,
                                device_name: dev.name,
                              })
                              setFound([])
                            }}
                          >
                            {dev.name} <span className="text-muted">{dev.os}</span>
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                  <div className="form-group">
                    <label>When</label>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <select
                        className="form-control"
                        id="aiSchedCadence"
                        value={d.cadence}
                        onChange={(e) => patch({ cadence: e.target.value as Draft['cadence'] })}
                      >
                        {Object.entries(CADENCES).map(([k, label]) => (
                          <option key={k} value={k}>
                            {label}
                          </option>
                        ))}
                      </select>
                      <select
                        className="form-control"
                        id="aiSchedWeekday"
                        hidden={d.cadence !== 'weekly'}
                        value={d.weekday}
                        onChange={(e) => patch({ weekday: Number(e.target.value) })}
                      >
                        {WEEKDAYS.map((w, i) =>
                          i > 0 ? (
                            <option key={i} value={i}>
                              {w}
                            </option>
                          ) : null,
                        )}
                      </select>
                      <select
                        className="form-control"
                        id="aiSchedMonthDay"
                        hidden={d.cadence !== 'monthly'}
                        value={d.month_day}
                        onChange={(e) => patch({ month_day: Number(e.target.value) })}
                      >
                        {Array.from({ length: 28 }, (_, i) => i + 1).map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </select>
                      <input type="time" className="form-control" id="aiSchedTime" value={d.time} onChange={(e) => patch({ time: e.target.value })} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label htmlFor="aiSchedRecipients">Send the answer to</label>
                    <input
                      type="text"
                      className="form-control"
                      id="aiSchedRecipients"
                      value={d.recipients}
                      onChange={(e) => patch({ recipients: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <SettingsCheck id="aiSchedEnabled" checked={d.enabled} onChange={(on) => patch({ enabled: on })} label="Enabled" />
                  </div>
                  <button type="submit" className="btn btn-primary" disabled={!data.ready}>
                    Save
                  </button>{' '}
                  {d.id > 0 ? (
                    <button type="button" className="btn btn-default" onClick={() => setDraft(null)}>
                      Cancel
                    </button>
                  ) : null}
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Content>
  )
}
